import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import { Tabs, TabsList, Tab, TabPanel } from '@/components/tabs';
import usePagination from '@/hooks/usePagination';
import {
  useGetGeoJsonFilesQuery,
  useLazyDownloadGeoJsonFileQuery,
  useGetManualAnnotationsQuery,
} from '@/services/RoutingManagementService';
import {
  GeoJsonFileMeta,
  ManualAnnotationFileMeta,
} from '@/services/RoutingManagementService';
import UploadRouteModal from '../modal/UploadRouteModal';
import DeleteRouteModal from '../modal/DeleteRouteModal';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import { formatFinnishNumber } from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

interface IRouteFile {
  id: number;
  filename: string;
  original_source: string;
  notes?: string | null;
  created_at?: string;
  size_kb?: number;
  type?: 'uploaded' | 'manual';
  name?: string;
  height_m?: number | null;
  depth_m?: number | null;
}

type GeoJsonFeatureLike = {
  properties?: Record<string, unknown> | null;
};

const getMinNumericProperty = (
  features: GeoJsonFeatureLike[] | undefined,
  propertyKeys: string[]
): number | null => {
  if (!features || !Array.isArray(features) || features.length === 0) {
    return null;
  }

  const values: number[] = [];

  for (const feature of features) {
    const props = feature?.properties;
    if (!props) continue;

    for (const key of propertyKeys) {
      const value = props[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        values.push(value);
      }
    }
  }

  if (values.length === 0) return null;
  return Math.min(...values);
};

export const RouteListings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    resetSearch,
    pagination,
    setPagination,
  } = usePagination();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<IRouteFile | null>(null);
  const [currentDownloadId, setCurrentDownloadId] = useState<number | null>(
    null
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<'uploaded' | 'manual'>(
    (searchParams.get('tab') as 'uploaded' | 'manual') || 'uploaded'
  );

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'manual' || tab === 'uploaded') {
      setActiveTab((tab as 'uploaded' | 'manual') || 'uploaded');
      navigate('/routing-management', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = location.state as { tab?: 'uploaded' | 'manual' } | null;
    if (state?.tab === 'manual') {
      setActiveTab('manual');
    }
  }, [location.state]);

  const uploadedQueryParams = {
    page: pagination.pageIndex + 1,
    page_size: pagination.pageSize,
  };

  const manualQueryParams = {
    page: pagination.pageIndex + 1,
    page_size: pagination.pageSize,
    search: debouncedValue,
  };

  const {
    data: geoJsonFiles,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetGeoJsonFilesQuery(uploadedQueryParams);

  const {
    data: manualAnnotations,
    isLoading: isManualLoading,
    isFetching: isManualFetching,
    isError: isManualError,
  } = useGetManualAnnotationsQuery(manualQueryParams);

  useEffect(() => {
    if (activeTab === 'uploaded' && geoJsonFiles) {
      handleTotalPages(geoJsonFiles.total_pages || 1);
      handleTotalItems(geoJsonFiles.total_items || 0);
    }
  }, [activeTab, geoJsonFiles, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (activeTab === 'manual' && manualAnnotations) {
      handleTotalPages(manualAnnotations.total_pages || 1);
      handleTotalItems(manualAnnotations.total_items || 0);
    }
  }, [activeTab, manualAnnotations, handleTotalItems, handleTotalPages]);

  useEffect(() => {
    if (activeTab !== 'manual') return;
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [activeTab, debouncedValue, setPagination]);

  useEffect(() => {
    if (isUploadModalOpen || isDeleteModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isUploadModalOpen, isDeleteModalOpen]);

  const routeFiles = useMemo(() => {
    if (!geoJsonFiles || !Array.isArray(geoJsonFiles.files)) {
      return [];
    }
    return geoJsonFiles.files.map((item: GeoJsonFileMeta) => ({
      id: item.id,
      filename: item.geojson_filename,
      original_source: item.excel_filename ? item.excel_filename : 'Imported',
      notes: item.notes,
      created_at: item.created_at,
      type: 'uploaded' as const,
    }));
  }, [geoJsonFiles]);

  const manualRouteFiles = useMemo(() => {
    if (!manualAnnotations || !Array.isArray(manualAnnotations.files)) {
      return [];
    }
    return manualAnnotations.files.map((item: ManualAnnotationFileMeta) => {
      const geojsonAny = (
        item as unknown as { geojson?: { features?: GeoJsonFeatureLike[] } }
      ).geojson;
      const derivedDepth = getMinNumericProperty(geojsonAny?.features, [
        'depth',
        'vayalue_sy',
      ]);
      const derivedHeight = getMinNumericProperty(geojsonAny?.features, [
        'min_height',
        'height',
      ]);

      return {
        id: item.id,
        filename: item.geojson_filename,
        original_source: 'Manual',
        notes: item.notes,
        created_at: item.created_at,
        type: 'manual' as const,
        name: item.name,
        height_m:
          derivedHeight ?? (item.height ? Number(item.height) : item.height_m),
        depth_m:
          derivedDepth ?? (item.depth ? Number(item.depth) : item.depth_m),
      };
    });
  }, [manualAnnotations]);

  const columns = useMemo(
    () => [
      {
        header: 'File',
        accessorKey: 'filename',
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div className="flex items-start gap-3" title={row.original.filename}>
            <div className="shrink-0 mt-0.5">
              <KeenIcon icon="document" className="text-gray-600 text-lg" />
            </div>
            <div className="min-w-0">
              <div
                className=" text-sm text-gray-900"
                data-testid="route-filename"
              >
                {row.original.filename}
              </div>
            </div>
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        header: 'Original Source',
        accessorKey: 'original_source',
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div
            className="text-sm text-gray-900 truncate"
            data-testid="route-source"
          >
            {row.original.original_source}
          </div>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        header: 'Notes',
        accessorKey: 'notes',
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div
            className="text-sm text-gray-900 truncate"
            data-testid="route-notes"
          >
            {row.original.notes || '-'}
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        header: 'Created At',
        accessorKey: 'created_at',
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div className="text-sm text-gray-900" data-testid="route-created-at">
            {row.original.created_at
              ? formatDisplayDate(new Date(row.original.created_at))
              : '-'}
          </div>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        header: 'Actions',
        id: 'actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('routing_management_read') && (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-black border border-gray-600"
                onClick={() => handleView(row.original)}
                aria-label="View route file"
              >
                <KeenIcon icon="eye" className="text-gray-600" />
              </button>
            )}
            {hasPermission('routing_management_read') &&
              row.original.type !== 'manual' && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-black border border-gray-600"
                  onClick={() => handleDownload(row.original)}
                  disabled={
                    isDownloading && currentDownloadId === row.original.id
                  }
                  aria-label="Download route file"
                >
                  {isDownloading && currentDownloadId === row.original.id ? (
                    <KeenIcon
                      icon="arrows-circle"
                      className="text-black animate-spin"
                    />
                  ) : (
                    <KeenIcon icon="file-down" className="text-gray-600" />
                  )}
                </button>
              )}
            {hasPermission('routing_management_delete') && (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-black border border-gray-600"
                onClick={() => handleDelete(row.original)}
                aria-label="Delete route file"
              >
                <KeenIcon icon="trash" className="text-gray-600" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [currentDownloadId, isDownloading, activeTab, hasPermission]
  );

  const manualColumns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div
            className="text-sm text-gray-900 truncate"
            title={row.original.name}
          >
            {row.original.name || '-'}
          </div>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        header: 'Height (m)',
        accessorKey: 'height_m',
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div
            className="text-sm text-gray-900"
            title={String(row.original.height_m ?? '')}
          >
            {typeof row.original.height_m === 'number'
              ? formatFinnishNumber(row.original.height_m, {
                  maximumFractionDigits: 2,
                })
              : '-'}
          </div>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        header: 'Depth (m)',
        accessorKey: 'depth_m',
        cell: ({ row }: { row: { original: IRouteFile } }) => (
          <div
            className="text-sm text-gray-900"
            title={String(row.original.depth_m ?? '')}
          >
            {typeof row.original.depth_m === 'number'
              ? formatFinnishNumber(row.original.depth_m, {
                  maximumFractionDigits: 2,
                })
              : '-'}
          </div>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      ...columns,
    ],
    [columns]
  );

  const handleView = (route: IRouteFile) => {
    if (route.original_source === 'Manual') {
      navigate(`/routing-management/manual-route-details/${route.id}`);
    } else {
      navigate(`/routing-management/details/${route.id}`);
    }
  };

  const handleDelete = (route: IRouteFile) => {
    setSelectedRoute(route);
    setIsDeleteModalOpen(true);
  };

  const [downloadGeoJsonFile] = useLazyDownloadGeoJsonFileQuery();

  const isValidBase64 = (str: string): boolean => {
    try {
      if (!str || str.length === 0) return false;
      const cleanStr = str.replace(/\s/g, '');
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(cleanStr)) return false;
      if (cleanStr.length % 4 !== 0) return false;
      const decoded = atob(cleanStr);
      const reencoded = btoa(decoded);
      return reencoded === cleanStr;
    } catch {
      return false;
    }
  };

  const handleDownload = async (route: IRouteFile) => {
    try {
      setCurrentDownloadId(route.id);
      setIsDownloading(true);

      const { data, error, isSuccess, isError } = await downloadGeoJsonFile(
        route.id
      );

      if (isError || !isSuccess) {
        let errorMessage = 'Failed to download file';
        if (error) {
          const err = error as {
            status?: number;
            data?: { message?: string };
            error?: string;
            message?: string;
          };
          if (err.status) {
            const dataMessage = err.data?.message || 'Unknown error';
            errorMessage = `Server returned status ${err.status}: ${dataMessage}`;
          } else if (err.data && typeof err.data === 'object') {
            errorMessage = err.data.message || errorMessage;
          } else if (err.error) {
            errorMessage = err.error || errorMessage;
          } else if (err.message) {
            errorMessage = err.message || errorMessage;
          }
        } else if (!data) {
          errorMessage = 'No data returned from server';
        }
        throw new Error(errorMessage);
      }

      const isExcelFile = route.original_source !== 'Imported';

      let mimeType: string;
      let filename: string;

      if (isExcelFile) {
        mimeType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        let baseFilename = route.original_source || `file-${route.id}.xlsx`;
        // Sanitize filename for download - remove/replace problematic characters
        baseFilename = baseFilename.replace(/[^a-zA-Z0-9._\-\s]/g, '_');
        filename = baseFilename.endsWith('.xlsx')
          ? baseFilename
          : baseFilename.replace(/\.[^.]*$/, '') + '.xlsx';
      } else {
        mimeType = 'application/geo+json';
        let baseFilename = route.filename || `file-${route.id}.geojson`;
        baseFilename = baseFilename.replace(/[^a-zA-Z0-9._\-\s]/g, '_');
        filename = baseFilename.endsWith('.geojson')
          ? baseFilename
          : baseFilename.replace(/\.[^.]*$/, '') + '.geojson';
      }

      // Handle different data types properly
      let blob: Blob;
      const dataAny = data as unknown;

      if (dataAny instanceof Blob) {
        if (dataAny.type !== mimeType) {
          blob = new Blob([dataAny], { type: mimeType });
        } else {
          blob = dataAny;
        }
      } else if (dataAny instanceof ArrayBuffer) {
        blob = new Blob([dataAny], { type: mimeType });
      } else if (typeof dataAny === 'string') {
        if (
          dataAny.startsWith('PK\x03\x04') ||
          dataAny.startsWith('\x50\x4B\x03\x04')
        ) {
          const bytes = new Uint8Array(dataAny.length);
          for (let i = 0; i < dataAny.length; i++) {
            bytes[i] = dataAny.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: mimeType });
        } else {
          const cleanData = dataAny.replace(/\s/g, '');
          if (isValidBase64(cleanData)) {
            const binaryString = atob(cleanData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            blob = new Blob([bytes], { type: mimeType });
          } else {
            blob = new Blob([cleanData], { type: mimeType });
          }
        }
      } else {
        throw new Error(`Unsupported data type: ${typeof dataAny}`);
      }

      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);

      try {
        link.click();
      } catch (e) {
        console.error('Error clicking download link:', e);
        window.open(url, '_blank');
      }

      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore — URL APIs may already be torn down in tests
        }
        // `document && …` still throws ReferenceError when `document` is unbound
        // (Vitest teardown after jsdom is disposed).
        if (
          typeof document !== 'undefined' &&
          document.body &&
          link.parentNode
        ) {
          document.body.removeChild(link);
        }
      }, 100);

      enqueueSnackbar('File downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading file:', error);
      enqueueSnackbar(`Failed to download file: ${(error as Error).message}`, {
        variant: 'error',
      });
    } finally {
      setCurrentDownloadId(null);
      setIsDownloading(false);
    }
  };
  const handleDeleteSuccess = () => {
    setIsDeleteModalOpen(false);
    setSelectedRoute(null);
    refetch();
  };

  const handleUploadSuccess = () => {
    setIsUploadModalOpen(false);
    refetch();
  };

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">Route Files</h2>
          <span className="text-xs text-gray-600">
            Uploaded GeoJSON route layers
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 ms-auto">
          {hasPermission('routing_management_create') && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-light border border-gray-300"
                onClick={() => setIsUploadModalOpen(true)}
                aria-label="Open upload route file modal"
              >
                <KeenIcon icon="cloud-upload" /> Add New Route
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light border border-gray-300"
                onClick={() => navigate('/routing-management/manual-route')}
                aria-label="Open manual route modal"
              >
                <KeenIcon icon="map" /> Manual Route
              </button>
            </>
          )}
        </div>
      </div>
      <div className="card-body">
        <Tabs
          value={activeTab}
          onChange={(_, value) =>
            setActiveTab((value as 'uploaded' | 'manual') || 'uploaded')
          }
          className="w-full"
        >
          <div className="flex flex-col gap-4">
            <TabsList className="flex gap-4 px-4">
              <Tab value="uploaded" className="text-sm">
                Uploaded Routes
              </Tab>
              <Tab value="manual" className="text-sm">
                Manual Routes
              </Tab>
            </TabsList>
            <TabPanel value="uploaded">
              {isLoading || isFetching ? (
                <div
                  className="text-center py-4"
                  role="status"
                  aria-live="polite"
                >
                  Loading route files...
                </div>
              ) : isError ? (
                <div className="text-center py-4 text-red-500" role="alert">
                  Failed to load route files. Please try again.
                </div>
              ) : routeFiles.length === 0 ? (
                <div className="text-center py-4 text-gray-500" role="status">
                  No route files found.
                </div>
              ) : (
                <DataGrid
                  columns={columns}
                  data={routeFiles}
                  rowSelect={false}
                  loading={isLoading || isFetching}
                  pagination={{
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    more: true,
                    totalItems: totalItems,
                    totalPages: totalPages,
                    setPagination,
                    info: `{from} - {to} of {count}`,
                    sizes: [5, 10, 25, 50, 100],
                    sizesLabel: 'Show',
                    sizesDescription: 'per page',
                  }}
                  sorting={[{ id: 'id', desc: true }]}
                  serverSide={true}
                  aria-label="Route files table"
                  messages={{
                    empty: 'No route files found',
                  }}
                />
              )}
            </TabPanel>
            <TabPanel value="manual">
              <div
                className="flex flex-wrap items-center gap-2 justify-between mb-4"
                role="toolbar"
                aria-label="Manual routes filter controls"
              >
                <div className="flex items-center gap-2 px-3">
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-light-active">
                    <div className="px-2 text-gray-600">
                      <KeenIcon icon="magnifier" className="text-lg" />
                    </div>
                    <input
                      id="search-manual-routes"
                      placeholder="Search manual routes"
                      type="text"
                      value={searchTerm}
                      onChange={handleSearch}
                      className="w-full sm:w-56 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none dark:bg-black contrast:bg-black dark:text-white/70 contrast:text-white/70 dark:placeholder:text-white/50 contrast:placeholder:text-white/50"
                      aria-label="Search manual routes by name or tags"
                    />
                  </div>
                </div>
                {searchTerm && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={resetSearch}
                    aria-label="Clear manual routes search"
                  >
                    <KeenIcon icon="cross" className="text-lg" />
                    Clear
                  </button>
                )}
              </div>

              {isManualLoading || isManualFetching ? (
                <div
                  className="text-center py-4"
                  role="status"
                  aria-live="polite"
                >
                  Loading manual routes...
                </div>
              ) : isManualError ? (
                <div className="text-center py-4 text-red-500" role="alert">
                  Failed to load manual routes. Please try again.
                </div>
              ) : manualRouteFiles.length === 0 ? (
                <div className="text-center py-4 text-gray-500" role="status">
                  No manual routes found.
                </div>
              ) : (
                <DataGrid
                  columns={manualColumns}
                  data={manualRouteFiles}
                  rowSelect={false}
                  loading={isManualLoading || isManualFetching}
                  pagination={{
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    more: true,
                    totalItems: totalItems,
                    totalPages: totalPages,
                    setPagination,
                    info: `{from} - {to} of {count}`,
                    sizes: [5, 10, 25, 50, 100],
                    sizesLabel: 'Show',
                    sizesDescription: 'per page',
                  }}
                  sorting={[{ id: 'id', desc: true }]}
                  serverSide={true}
                  aria-label="Manual route files table"
                  hasActiveFilters={!!searchTerm}
                  messages={{
                    empty: 'No manual routes found',
                  }}
                />
              )}
            </TabPanel>
          </div>
        </Tabs>
      </div>
      <UploadRouteModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />
      <DeleteRouteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDeleteSuccess={handleDeleteSuccess}
        route={selectedRoute || { id: 0, filename: '' }}
      />
    </div>
  );
};
